import { useState, useEffect } from "react";
import Alert from "../../common/components/Alert";
import { Link, useNavigate } from "react-router";
import { ENDPOINTS } from "@/common/utils/url-registry";
import { VisitPage } from "@/common/utils/types";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import LogoBlue from "../../assets/HD-Horizontal-Blue.png";
import { HALLOWEEN_LOGO_SRC, isHalloween } from "@/common/utils/halloween";
import HDLoadingButton from "../../common/components/HDLoadingButton";
import { fetchAccountResolvedEnvelope, getDefaultView } from "@/common/lib/supabaseClient";
import { signOutLocal } from "@/common/lib/signOut";
import { apiFetch, expectJsonBody } from "@/common/lib/api";
import {
    getStoredSelectedClinicAccountId,
    setStoredSelectedClinicAccountId,
    clearStoredSelectedClinicAccountId,
} from "@/common/lib/selectedClinicAccountStorage";

const ALLOWED_ERROR_MESSAGES: Record<string, string> = {
    account_users_disabled: "Team access is disabled for this clinic. Contact the clinic owner.",
};

function Login() {
    const navigate = useNavigate();
    const [formState, setFormState] = useState({
        email: "",
        password: "",
    });
    const [error, setError] = useState<string | null>(null);
    const supabase = useSupabaseClient();
    const [loading, setLoading] = useState(false);

    const [magicLinkState, setMagicLinkState] = useState<{
        status: "idle" | "sending" | "sent";
        cooldownUntil: number | null;
    }>({ status: "idle", cooldownUntil: null });
    const [nowTick, setNowTick] = useState(Date.now());

    useEffect(() => {
        if (!magicLinkState.cooldownUntil) {
            return;
        }
        const cooldownUntil = magicLinkState.cooldownUntil;
        const interval = setInterval(() => {
            const now = Date.now();
            setNowTick(now);
            if (now >= cooldownUntil) {
                clearInterval(interval);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [magicLinkState.cooldownUntil]);

    const magicLinkCooldownSecondsRemaining = magicLinkState.cooldownUntil
        ? Math.max(0, Math.ceil((magicLinkState.cooldownUntil - nowTick) / 1000))
        : 0;

    async function handleMagicLinkRequest() {
        if (!formState.email) {
            setError("Enter your email address first.");
            return;
        }
        setError(null);
        setMagicLinkState((s) => ({ ...s, status: "sending" }));
        try {
            await supabase.auth.signInWithOtp({
                email: formState.email,
                options: {
                    emailRedirectTo: `${window.location.origin}/accept-invite`,
                    shouldCreateUser: false,
                },
            });
        } catch {
            // Ignore transport failures too — swallowing every outcome here (success, unregistered
            // email, or network error) into one identical state is what prevents account enumeration.
        }
        // Always land on the same "sent" state so an unregistered email is indistinguishable from a
        // registered one — both the UI state and the cooldown must match, not just the error text.
        setMagicLinkState({ status: "sent", cooldownUntil: Date.now() + 60_000 });
    }

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const errorParam = params.get("error");
        if (errorParam && ALLOWED_ERROR_MESSAGES[errorParam]) {
            setError(ALLOWED_ERROR_MESSAGES[errorParam]);
            window.history.replaceState({}, "", location.pathname);
        }
    }, []);

    async function signInWithEmail(event: React.FormEvent<HTMLFormElement>) {
        const params = new URLSearchParams(location.search);
        event.preventDefault();
        setLoading(true);
        const { data, error } = await supabase.auth.signInWithPassword({
            email: formState.email,
            password: formState.password,
        });

        if (error) {
            if (error.code === "user_banned") {
                setError("Account is deactivated. Contact support@happydoc.ai.");
                setLoading(false);
                return;
            }

            if (error.code === "email_not_confirmed") {
                setLoading(false);
                const params = new URLSearchParams({ verifyEmail: formState.email });
                navigate(`/signup?${params.toString()}`);
                return;
            }

            setError(error.message);
            setLoading(false);
            return;
        }

        setLoading(false);

        // If bridge OAuth sent returnTo, redirect there after login (e.g. /bridge-auth?redirect_uri=...)
        // Only allow relative paths to prevent open redirect (e.g. /login?returnTo=https://evil.com)
        const returnTo = params.get("returnTo");
        const isSafeReturnTo = returnTo != null && returnTo.startsWith("/") && !returnTo.startsWith("//");
        if (isSafeReturnTo) {
            window.location.href = returnTo;
            return;
        }

        let authUserId: string | undefined = data?.user?.id;
        if (!authUserId) {
            const { data: sessionData } = await supabase.auth.getSession();
            authUserId = sessionData.session?.user?.id;
        }
        let selectedClinicAccountId: string | null = null;
        if (authUserId) {
            try {
                // Ensure session is fully ready before making API calls
                await supabase.auth.getSession();

                const memberships = expectJsonBody(
                    await apiFetch<{ account_ids: string[] }>("account/memberships"),
                    "account/memberships",
                );
                if (!memberships.account_ids || !Array.isArray(memberships.account_ids)) {
                    throw new Error("Invalid memberships response: missing account_ids");
                }
                const ids = memberships.account_ids;
                if (ids.length === 0) {
                    // Server returns no_access_reason when team access was disabled after the user lost membership;
                    // that case has account_users_enabled === false, so we must not gate this on a local probe.
                    const resolved = await fetchAccountResolvedEnvelope(authUserId);
                    if (resolved && !resolved.account && resolved.no_access_reason === "account_users_disabled") {
                        await signOutLocal();
                        window.location.href = `/${ENDPOINTS.Login}?error=account_users_disabled`;
                        return;
                    }
                }
                if (ids.length > 1) {
                    const stored = getStoredSelectedClinicAccountId(authUserId);
                    if (!stored || !ids.includes(stored)) {
                        clearStoredSelectedClinicAccountId(authUserId);
                        window.location.href = `/${ENDPOINTS.SelectClinic}`;
                        return;
                    }
                    selectedClinicAccountId = stored;
                } else if (ids.length === 1) {
                    selectedClinicAccountId = ids[0];
                    setStoredSelectedClinicAccountId(authUserId, selectedClinicAccountId);
                }
            } catch {
                /* continue to app if memberships request fails */
            }
        }

        // navigate to list or calendar view, depending on account.meta_data.default_view
        const defaultView =
            authUserId != null ? await getDefaultView(authUserId, selectedClinicAccountId) : VisitPage.List;
        params.set("page", defaultView);
        const search = params.toString();
        window.location.href = search ? `/?${search}` : "/";
    }

    return (
        <div className="flex item-center min-h-screen flex-1 flex-col justify-center px-6 py-12 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                <img
                    className="mx-auto h-24 w-auto"
                    src={isHalloween() ? HALLOWEEN_LOGO_SRC : LogoBlue}
                    alt="HappyDoc"
                />
                <h1 className="mt-10 text-center text-xl font-semibold leading-9 tracking-tight text-gray-900">
                    Sign in to your account
                </h1>
            </div>

            <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                <form className="space-y-6" onSubmit={signInWithEmail}>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                            Email address
                        </label>
                        <div className="mt-2">
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focused-inset  sm:text-sm sm:leading-6 pl-2"
                            />
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
                                Password
                            </label>
                        </div>
                        <div className="mt-2">
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                onChange={(e) => setFormState({ ...formState, password: e.target.value })}
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focused-inset  sm:text-sm sm:leading-6 pl-2"
                            />
                        </div>
                        <div className="text-sm text-right mt-2">
                            <Link
                                to={`/${ENDPOINTS.ResetPassword}`}
                                className="font-semibold text-max-900 hover:text-max-950"
                            >
                                Forgot password?
                            </Link>
                        </div>
                    </div>

                    <div>
                        <HDLoadingButton
                            loading={loading}
                            type="submit"
                            className="w-full"
                            disabled={formState?.email === "" || formState?.password === ""}
                        >
                            Sign in
                        </HDLoadingButton>
                    </div>
                </form>
                <div className="mt-4 text-center text-sm">
                    {magicLinkState.status === "sending" ? (
                        <button
                            type="button"
                            disabled
                            className="font-semibold text-max-900 opacity-60 cursor-not-allowed"
                        >
                            Sending…
                        </button>
                    ) : magicLinkCooldownSecondsRemaining > 0 ? (
                        <p className="text-gray-600">
                            Check your email for a login link. You can request another in{" "}
                            {magicLinkCooldownSecondsRemaining}s.
                        </p>
                    ) : (
                        <button
                            type="button"
                            onClick={handleMagicLinkRequest}
                            className="font-semibold text-max-900 hover:text-max-950"
                        >
                            {magicLinkState.status === "sent" ? "Resend login link" : "Email me a login link"}
                        </button>
                    )}
                </div>
                <div className="mt-4">{error && <Alert alertType="error" title="Error" description={error} />}</div>
            </div>
        </div>
    );
}

export default Login;
